import ChargeHelper from 'features/Charge/chargeHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useRouter from 'hooks/useRouter';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import DialogAddVehicleBrandView from '../DialogAddVehicleBrandView';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import AppConstants from 'general/constants/AppConstants';

ChargeVehicleBrandListView.propTypes = {
  showFullUI: PropTypes.bool,
};

function ChargeVehicleBrandListView({ showFullUI = false }) {
  // MARK: ---- PROPERTIES ----
  const router = useRouter();
  const { user, permissionList } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const [filter, setFilter] = useState(ChargeHelper.Filter.chargeVehicleBrandList);
  const [chargeVehicleBrandList, setChargeVehicleBrandList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  useEffect(() => {
    setIsLoading(true);
    ChargeHelper.API.VehicleBrand.requestGetVehicleBrandList(Utils.filterParams(filter))
      .then((response) => {
        setChargeVehicleBrandList(response?.brands);
        setTotal(response?.total);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filter]);

  const typeMap = {
    CAR: "Ô tô",
    BIKE: "Xe máy"
  };

  const fullColumns = useMemo(() => {
    return [
      {
        id: 'LOGO',
        name: UIHelper.buildTableColumn('Logo'),
        selector: (row) =>
          UIHelper.buildAvatar(Utils.getFullUrl(row?.logo), 40, null, false, false),
        width: '70px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'NAME',
        name: UIHelper.buildTableColumn('Tên hãng xe'),
        selector: (row) => row?.name,
        minWidth: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'VEHICLE_TYPE',
        name: UIHelper.buildTableColumn('Loại phương tiện'),
        selector: (row) => row?.vehicleTypes?.length > 0
          ? row?.vehicleTypes?.map((type, index) => index === 0 ? typeMap[type] : typeMap[type].toLowerCase()).join(", ")
          : '',
        width: '150px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'MODEL_NUM',
        name: UIHelper.buildTableColumn('Số dòng xe'),
        selector: (row) => Utils.formatNumber(row?.models?.length),
        width: '120px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'DESCRIPTION',
        name: UIHelper.buildTableColumn('Mô tả'),
        selector: (row) => {
          return (
            <KTTooltip text={row?.description} isHtmlContent={true}>
              <div 
                className="my-2 text-ellipsis-2-lines" 
                style={{ whiteSpace: 'normal' }}
                dangerouslySetInnerHTML={{ __html: row?.description }}
              />
            </KTTooltip>
          );
        },
        minWidth: '300px',
        style: tableStyle.columns.normal,
      },
      {
        name: UIHelper.buildTableColumn('Thao tác'),
        maxWidth: '140px',
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        cell: (row) => {
          return (
            <div className="d-flex gap-2 align-items-center justify-content-start">
              {Utils.checkPermission(permissionList, AppConstants.permissionCode.vehicleBrandView.code) && (
                <KTTooltip offset={[0, 0]} text="Xem chi tiết">
                  <a
                    className="btn btn-sm btn-clean btn-icon"
                    onClick={() => {
                      router.navigate(`${row?.brandId}`);
                    }}
                  >
                    <i
                      className="fa-regular fa-eye"
                      style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                    />
                  </a>
                </KTTooltip>
              )}
              {Utils.checkPermission(permissionList, AppConstants.permissionCode.vehicleBrandUpdate.code) && (
                <KTTooltip offset={[0, 0]} text="Chỉnh sửa">
                  <a
                    className="btn btn-sm btn-clean btn-icon"
                    onClick={() => {
                      handleAddNew(row);
                    }}
                  >
                    <i
                      className="fa-regular fa-edit"
                      style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                    />
                  </a>
                </KTTooltip>
              )}
              {Utils.checkPermission(permissionList, AppConstants.permissionCode.vehicleBrandDelete.code) && (
                <KTTooltip offset={[0, 0]} text="Xoá">
                  <a
                    className="btn btn-sm btn-clean btn-icon"
                    onClick={() => {
                      handleDelete(row);
                    }}
                  >
                    <i
                      className="fa-regular fa-trash-can"
                      style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                    />
                  </a>
                </KTTooltip>
              )}
            </div>
          );
        },
      },
    ];
  }, []);

  // MARK: ---- METHODS ----
  const handleRefresh = () => {
    setFilter({ ...ChargeHelper.Filter.chargeVehicleBrandList });
  };
  const handleDelete = (row) => {
    ChargeHelper.UI.VehicleBrand.showDialogConfirmDeleteVehicleBrand(row, () => {
      handleRefresh();
    });
  };
  const handleAddNew = (row) => {
    AppCustomDialogHelper.show(
      !!row ? 'Sửa hãng xe' : 'Thêm hãng xe',
      <DialogAddVehicleBrandView
        item={row}
        onCancel={() => AppCustomDialogHelper.hide()}
        onDone={() => {
          AppCustomDialogHelper.hide();
          handleRefresh();
        }}
      />,
      'md',
      true,
      true
    );
  };

  return (
    <div className="px-4 my-4">
      {UIHelper.buildCard(
        <div>
          {/* header */}
          {showFullUI && (
            <div className="px-4 pt-4 d-flex justify-content-between align-items-center">
              {/* title */}
              <div className="d-flex flex-column align-items-start">
                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                  Danh sách hãng xe
                </span>
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                  Tổng số:{' '}
                  <span style={{ color: AppColors.primary }}>
                    {Utils.formatNumber(total)} hãng xe
                  </span>
                </span>
              </div>

              {/* actions */}
              {isAdmin && Utils.checkPermission(permissionList, AppConstants.permissionCode.vehicleBrandCreate.code) &&
                UIHelper.buildGreenButton(
                  'Thêm hãng xe',
                  handleAddNew,
                  <i className="fa-regular fa-circle-plus" />
                )}
            </div>
          )}
          {/* body */}
          <div className={`${showFullUI ? 'p-4' : 'pt-4'}`}>
            {/* filter */}
            <div className="row m-0">
              {/* tim kiem */}
              <div className="pl-0 pr-0 pr-md-4 col-xl-3 col-md-4 mb-3">
                {UIHelper.buildFormLabel('Tìm kiếm')}
                <KTFormSearch
                  name="search"
                  placeholder="Theo tên hãng sản xe, mô tả..."
                  value={ChargeHelper.Filter.chargeVehicleBrandList.q}
                  onSubmit={(text) => {
                    ChargeHelper.Filter.chargeVehicleBrandList = {
                      ...ChargeHelper.Filter.chargeVehicleBrandList,
                      page: 0,
                      q: text,
                    };
                    handleRefresh();
                  }}
                />
              </div>
              <div className="pl-0 pr-0 pr-md-4 col-xl-3 col-md-4 mb-3">
                {UIHelper.buildFormLabel('Loại phương tiện')}
                <KTBSDropdown
                  name="vehicleType"
                  toggleElement={
                    <button
                      className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                      type="button"
                    >
                      {_.find(
                        ChargeHelper.Data.vehicleTypeOptions,
                        (item) =>
                          item.value === ChargeHelper.Filter.chargeVehicleBrandList?.vehicleType
                      )?.name ?? 'Tất cả'}
                    </button>
                  }
                  selectedValue={ChargeHelper.Filter.chargeVehicleBrandList?.vehicleType}
                  dropdownMenuItems={[
                    { name: 'Tất cả', value: '' },
                    ...ChargeHelper.Data.vehicleTypeOptions,
                  ]}
                  dropdownMenuClassName="overflow-auto w-100"
                  dropdownMenuStyle={{ maxHeight: '30vh' }}
                  onChange={(value) => {
                    ChargeHelper.Filter.chargeVehicleBrandList = {
                      ...ChargeHelper.Filter.chargeVehicleBrandList,
                      vehicleType: value,
                      page: 0,
                    };
                    handleRefresh();
                  }}
                  isDropdownForm={true}
                />
              </div>
            </div>

            {/* table */}
            <div className="mb-3">
              <KTTable
                columns={fullColumns}
                items={chargeVehicleBrandList}
                selectableRows={false}
                striped={false}
                compactGrid={false}
                onClickedRow={(row) => { }}
                onSelectedRowsChanged={(rows) => { }}
                onDoubleClickedRow={(row) => { }}
                noDataComponent={UIHelper.buildEmptyNoData(
                  'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                )}
              />
            </div>

            {/* phan trang */}
            <KTPagination
              currentPage={filter.page + 1}
              rowCount={total}
              rowsPerPage={filter.limit}
              currentItemInPageCount={chargeVehicleBrandList?.length}
              onChangePage={(newPage) => {
                const iNewPage = parseInt(newPage);
                ChargeHelper.Filter.chargeVehicleBrandList = {
                  ...ChargeHelper.Filter.chargeVehicleBrandList,
                  page: iNewPage - 1,
                };
                handleRefresh();
              }}
              onChangeRowsPerPage={(newRowsPerPage) => {
                const iNewRowsPerPage = parseInt(newRowsPerPage);
                ChargeHelper.Filter.chargeVehicleBrandList = {
                  ...ChargeHelper.Filter.chargeVehicleBrandList,
                  limit: iNewRowsPerPage,
                  page: 0,
                };
                handleRefresh();
              }}
              loading={isLoading}
              color={KTPaginationColor.success}
              appendText="hãng xe"
            />
          </div>
        </div>,
        false,
        !showFullUI
      )}
    </div>
  );
}

export default ChargeVehicleBrandListView;
